import { computeSessionTrainerHonorarEur } from "./trainer-session-honorar.js";
import { getSessionCoordinatorFeeEur } from "./trainer-coordinator-fee.js";

export const SETTLEMENT_PAYOUT_ROLE = Object.freeze({
  PRIMARY: "primary",
  ASSISTANT: "assistant",
});

/**
 * @param {unknown} id
 * @returns {string}
 */
export function normalizeTrainerId(id) {
  return String(id ?? "").trim();
}

/**
 * @param {unknown} value
 * @returns {number|null}
 */
export function parseAssistantHonorarEur(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100) / 100;
}

/**
 * Brutto-Honorar der Session (Trainer-Vergütung gesamt).
 * @param {Record<string, unknown>|null|undefined} session
 * @returns {number}
 */
export function computeSessionTrainerHonorarGrossEur(session) {
  return computeSessionTrainerHonorarEur(session);
}

/**
 * @param {Record<string, unknown>|null|undefined} session
 * @returns {number}
 */
export function getSessionAssistantHonorarEur(session) {
  const aid = normalizeTrainerId(session?.assistant_trainer_id);
  if (!aid) return 0;
  const share = parseAssistantHonorarEur(session?.assistant_honorar_eur);
  if (share === null || share <= 0) return 0;
  const gross = computeSessionTrainerHonorarGrossEur(session);
  if (gross <= 0) return 0;
  return Math.min(share, gross);
}

/**
 * Netto für den Haupttrainer nach Assistenten-Anteil.
 * @param {Record<string, unknown>|null|undefined} session
 * @returns {number}
 */
export function computeSessionTrainerNetHonorarEur(session) {
  const gross = computeSessionTrainerHonorarGrossEur(session);
  if (gross <= 0) return 0;
  const assistant = getSessionAssistantHonorarEur(session);
  const coordinator = getSessionCoordinatorFeeEur(session);
  return Math.round(Math.max(0, gross - assistant - coordinator) * 100) / 100;
}

/**
 * @param {number|null|undefined} grossEur
 * @param {unknown} assistantShareEur
 * @returns {{ ok: true, share: number|null }|{ ok: false, code: string }}
 */
export function validateAssistantHonorarShare(grossEur, assistantShareEur) {
  const gross = Number(grossEur);
  const share = parseAssistantHonorarEur(assistantShareEur);
  if (share === null) return { ok: true, share: null };
  if (!Number.isFinite(gross) || gross <= 0) {
    return { ok: false, code: "gross-missing" };
  }
  if (share > gross + 1e-9) {
    return { ok: false, code: "share-exceeds-gross" };
  }
  return { ok: true, share };
}

/**
 * @param {string|null|undefined} mainTrainerId
 * @param {string|null|undefined} assistantTrainerId
 * @returns {boolean}
 */
export function assistantTrainerIdsValid(mainTrainerId, assistantTrainerId) {
  const main = normalizeTrainerId(mainTrainerId);
  const assistant = normalizeTrainerId(assistantTrainerId);
  if (!assistant) return true;
  if (!main) return false;
  return main !== assistant;
}

/**
 * Session-Felder für Persistenz (null = kein Assistent).
 * @param {{ assistantTrainerId?: string, assistantHonorarEur?: unknown, grossHonorarEur?: number }} p
 */
export function buildSessionAssistantPersistFields({
  assistantTrainerId,
  assistantHonorarEur,
  grossHonorarEur,
}) {
  const aid = normalizeTrainerId(assistantTrainerId);
  if (!aid) {
    return { assistant_trainer_id: null, assistant_honorar_eur: null };
  }
  const validated = validateAssistantHonorarShare(
    grossHonorarEur,
    assistantHonorarEur,
  );
  if (!validated.ok || validated.share === null || validated.share <= 0) {
    return { assistant_trainer_id: null, assistant_honorar_eur: null };
  }
  return { assistant_trainer_id: aid, assistant_honorar_eur: validated.share };
}

/**
 * @param {Record<string, unknown>|null|undefined} trainer
 * @returns {{ assistantTrainerId: string, assistantHonorarEur: number|null }}
 */
export function getTrainerDefaultAssistantFields(trainer) {
  return {
    assistantTrainerId: normalizeTrainerId(
      trainer?.default_assistant_trainer_id,
    ),
    assistantHonorarEur: parseAssistantHonorarEur(
      trainer?.default_assistant_honorar_eur,
    ),
  };
}

/**
 * Eigener Standard-Satz eines Assistenztrainers (in dessen Stammdaten hinterlegt),
 * z.B. wenn der Haupttrainer keinen fixen Betrag vorgibt.
 * @param {Record<string, unknown>|null|undefined} assistantTrainer
 * @returns {number|null}
 */
export function getAssistantOwnDefaultRateEur(assistantTrainer) {
  return parseAssistantHonorarEur(assistantTrainer?.assistant_default_rate_eur);
}

/**
 * @param {string} trainerId
 * @param {Record<string, unknown>} session
 * @returns {boolean}
 */
export function isAssistantSettlementSessionForTrainer(trainerId, session) {
  const tid = normalizeTrainerId(trainerId);
  if (!tid) return false;
  if (!session?.attendance_confirmed) return false;
  if (String(session.assistant_settlement_id || "").trim()) return false;
  if (normalizeTrainerId(session.assistant_trainer_id) !== tid) return false;
  return getSessionAssistantHonorarEur(session) > 0;
}

/**
 * @param {string} trainerId
 * @param {Record<string, unknown>} session
 * @returns {boolean}
 */
export function isPrimarySettlementSessionForTrainer(trainerId, session) {
  const tid = normalizeTrainerId(trainerId);
  if (!tid) return false;
  if (!session?.attendance_confirmed) return false;
  if (String(session.trainer_settlement_id || "").trim()) return false;
  return normalizeTrainerId(session.trainer_id) === tid;
}
